import React, { useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { apiService } from "../../../services/api";
import { EmpenhoFormatado } from "../../../types/empenhos";

interface DocumentoEmpenho {
    id: number;
    id_empenho: number;
    numero_empenho?: string;
    titulo: string;
    data_upload: string;
    tipo_documento: string;
    nome_arquivo: string;
    usuario_nome?: string;
    arquivos?: Array<{
        nome_original: string;
        nome_salvo: string;
        tipo: string;
        tamanho: number;
        caminho: string;
    }> | string;
    descricao?: string;
}

interface UploadedFile {
    file: File;
    size: string;
    name: string;
}

interface ModalDocumentoEmpenhoProps {
    show: boolean;
    onHide: () => void;
    contratoId: number;
    empenhos: EmpenhoFormatado[];
    documento: DocumentoEmpenho | null;
    onSave: () => void;
}

const ModalDocumentoEmpenho: React.FC<ModalDocumentoEmpenhoProps> = ({
    show,
    onHide,
    contratoId,
    empenhos,
    documento,
    onSave
}) => {
    const [formData, setFormData] = useState({
        id_empenho: 0,
        id_contrato: contratoId,
        titulo: "",
        descricao: "",
        tipo_documento: "Nota Fiscal"
    });

    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [arquivosAtuais, setArquivosAtuais] = useState<any[]>([]);
    const [arquivosRemover, setArquivosRemover] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const isEditMode = !!documento;

    // Função auxiliar para formatar o tamanho do arquivo
    const formatFileSize = (bytes: number): string => {
        if (!bytes || bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Inicializar formulário quando o modal for aberto
    useEffect(() => {
        if (show) {
            if (isEditMode && documento) {
                setFormData({
                    id_empenho: documento.id_empenho,
                    id_contrato: contratoId,
                    titulo: documento.titulo || "",
                    descricao: documento.descricao || "",
                    tipo_documento: documento.tipo_documento || "Nota Fiscal"
                });

                // Se há arquivos existentes, configure-os
                if (documento.arquivos && Array.isArray(documento.arquivos)) {
                    setArquivosAtuais(documento.arquivos);
                } else if (documento.arquivos && typeof documento.arquivos === 'string') {
                    try {
                        const arquivos = JSON.parse(documento.arquivos);
                        setArquivosAtuais(arquivos);
                    } catch (error) {
                        console.error("Erro ao processar arquivos:", error);
                        setArquivosAtuais([]);
                    }
                } else {
                    setArquivosAtuais([]);
                }
            } else {
                // Novo documento - limpar o formulário
                setFormData({
                    id_empenho: 0,
                    id_contrato: contratoId,
                    titulo: "",
                    descricao: "",
                    tipo_documento: "Nota Fiscal"
                });
                setArquivosAtuais([]);
            }

            setUploadedFiles([]);
            setArquivosRemover([]);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [show, contratoId, documento, isEditMode]);

    // Handlers de formulário
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Manipuladores de arquivo
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files).map(file => ({
                file: file,
                size: formatFileSize(file.size),
                name: file.name
            }));
            setUploadedFiles(prev => [...prev, ...newFiles]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveArquivoAtual = (nomeSalvo: string) => {
        setArquivosAtuais(prev => prev.filter(a => a.nome_salvo !== nomeSalvo));
        setArquivosRemover(prev => [...prev, nomeSalvo]);
    };

    // Enviar formulário
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (!formData.id_empenho) {
                toast.error("Selecione um empenho para anexar o documento");
                return;
            }

            if (!formData.titulo.trim()) {
                toast.error("O título é obrigatório");
                return;
            }

            if (uploadedFiles.length === 0 && arquivosAtuais.length === 0) {
                toast.error("Selecione pelo menos um arquivo para upload");
                return;
            }

            setIsSubmitting(true);

            const files = uploadedFiles.map(fileData => fileData.file);

            if (isEditMode && documento) {
                // Atualizar documento existente
                await apiService.updateAnexoEmpenho(documento.id, {
                    titulo: formData.titulo,
                    descricao: formData.descricao,
                    tipo_documento: formData.tipo_documento,
                    arquivos: files,
                    remover_arquivos: arquivosRemover
                });
                toast.success("Documento atualizado com sucesso!");
            } else {
                // Criar novo documento
                await apiService.createAnexoEmpenho({
                    id_empenho: formData.id_empenho,
                    id_contrato: contratoId,
                    titulo: formData.titulo,
                    descricao: formData.descricao,
                    tipo_documento: formData.tipo_documento,
                    arquivos: files
                });
                toast.success("Documento adicionado com sucesso!");
            }

            onSave();
        } catch (error) {
            console.error("Erro ao salvar documento:", error);
            toast.error("Erro ao salvar documento");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            className="custom-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {isEditMode ? "Editar Documento de Empenho" : "Adicionar Documento de Empenho"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="col-form-label">
                            Empenho <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-select"
                            name="id_empenho"
                            value={formData.id_empenho}
                            onChange={handleChange}
                            required
                            disabled={isEditMode}
                        >
                            <option value="">Selecione um empenho</option>
                            {empenhos.map(emp => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.codigo_empenho || emp.numero_empenho || `EMP-${emp.id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="col-form-label">
                            Título <span className="text-danger">*</span>
                        </label>
                        <input
                            className="form-control"
                            type="text"
                            name="titulo"
                            value={formData.titulo}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label className="col-form-label">
                            Tipo de Documento <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-select"
                            name="tipo_documento"
                            value={formData.tipo_documento}
                            onChange={handleChange}
                        >
                            <option value="Nota Fiscal de Saída">Nota Fiscal de Saída</option>
                            <option value="Comprovante">Comprovante</option>
                            <option value="Recibo">Recibo</option>
                            <option value="Outro">Outro</option>
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="col-form-label">
                            Descrição
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            name="descricao"
                            value={formData.descricao}
                            onChange={handleChange}
                        ></textarea>
                    </div>

                    {/* Arquivos atuais (somente no modo de edição) */}
                    {isEditMode && arquivosAtuais.length > 0 && (
                        <div className="mb-3">
                            <label className="col-form-label">Arquivos Atuais</label>
                            {arquivosAtuais.map((arquivo, index) => (
                                <div key={index} className="upload-file upload-list">
                                    <div>
                                        <h6>{arquivo.nome_original}</h6>
                                        <p>{formatFileSize(arquivo.tamanho)}</p>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveArquivoAtual(arquivo.nome_salvo)}
                                        className="text-danger border-0 bg-transparent"
                                        type="button"
                                    >
                                        <i className="ti ti-trash-x" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload de novos arquivos */}
                    <div className="mb-3">
                        <label className="col-form-label">
                            {isEditMode ? "Adicionar Novos Arquivos" : "Anexo"}
                            {(!isEditMode || arquivosAtuais.length === 0) && <span className="text-danger">*</span>}
                        </label>
                        <div className="drag-attach">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                multiple
                                accept="*/*"
                            />
                            <div className="img-upload">
                                <i className="ti ti-file-broken" />
                                Enviar Arquivo
                            </div>
                        </div>
                    </div>

                    {/* Arquivos que serão enviados */}
                    {uploadedFiles.length > 0 && (
                        <div className="mb-3">
                            <label className="col-form-label">Arquivos para Upload</label>
                            {uploadedFiles.map((fileData, index) => (
                                <div key={index} className="upload-file upload-list">
                                    <div>
                                        <h6>{fileData.name}</h6>
                                        <p>{fileData.size}</p>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveFile(index)}
                                        className="text-danger border-0 bg-transparent"
                                        type="button"
                                    >
                                        <i className="ti ti-trash-x" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="col-lg-12 text-end modal-btn mt-4">
                        <button
                            type="button"
                            className="btn btn-light me-2"
                            onClick={onHide}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Salvando...
                                </>
                            ) : (
                                isEditMode ? 'Atualizar' : 'Confirmar'
                            )}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default ModalDocumentoEmpenho;